/* ═══════════════════════════════════════════════════════════════
   The Designer — the attribute set every spec block node shares, and
   the id minter. Split out of schema.ts so schema.ts and tables.ts can
   both depend on it without importing each other's whole module.
   ═══════════════════════════════════════════════════════════════ */

export type BlockMeta = Record<string, unknown>;

/** Shared attributes every spec block node carries (paragraphs, tables,
 *  images, smart tokens — every direct child of the document). */
export const blockAttrs = {
  blockId: { default: '' },
  level: { default: 1 },
  continuation: { default: false },
  excluded: { default: false },
  /**
   * GROUP membership — a first-class scalar, exactly like `level` and
   * `excluded`, never a `meta` key: the gutter reads it off the DOM to paint
   * one grip per group, and a scalar is the only thing that survives the
   * clipboard's DOM round trip intact.
   *
   * Rendered as `data-group-id` (and parsed back from it) so the attribute a
   * human reads in the inspector is the attribute the paste pipeline reads
   * back. An ungrouped block emits nothing at all rather than an empty
   * attribute, so `[data-group-id]` means "in a group" with no second test.
   */
  groupId: {
    default: '',
    renderHTML: (attributes: Record<string, unknown>) => {
      const id = attributes.groupId as string | undefined;
      return id ? { 'data-group-id': id } : {};
    },
    parseHTML: (element: HTMLElement): string => element.getAttribute('data-group-id') || '',
  },
  /**
   * The bookkeeping bag — and the only attribute here that is not a scalar.
   * Left to the framework's default attribute rendering, an object reaches
   * the DOM as the literal string "[object Object]" and comes back from any
   * clipboard round-trip (which IS a DOM round-trip: the editor serializes to
   * HTML and re-parses it) as that string, so a copied block pastes with its
   * meta destroyed. JSON in a data attribute is what makes it survive — for
   * the block clipboard and for a plain multi-paragraph text paste alike.
   */
  meta: {
    default: {} as BlockMeta,
    renderHTML: (attributes: Record<string, unknown>) => {
      const meta = attributes.meta as BlockMeta | undefined;
      if (!meta || typeof meta !== 'object' || !Object.keys(meta).length) return {};
      return { 'data-meta': JSON.stringify(meta) };
    },
    parseHTML: (element: HTMLElement): BlockMeta => {
      const raw = element.getAttribute('data-meta');
      if (!raw) return {};
      try {
        const parsed: unknown = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? (parsed as BlockMeta)
          : {};
      } catch {
        return {};
      }
    },
  },
};

/** Same shape the rest of the app generates, so ids stay recognisable. */
export function newBlockId(): string {
  return 'b-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

/** A group's id — the same mint, a different prefix, so a value read off
 *  `data-group-id` is never mistaken for a block id in a log or a diff. */
export function newGroupId(): string {
  return 'g-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}
